import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { lineAmount } from "./kitLine";

const CartContext = createContext(null);
const STORAGE_VERSION = 1;

function storageKey(storeSlug) {
  return `atomicpay-storefront-cart:v${STORAGE_VERSION}:${storeSlug}`;
}

function readCart(storeSlug) {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey(storeSlug)));
    if (!Array.isArray(value?.items)) return [];
    return value.items.flatMap((item) => {
      if (item?.offerId) {
        const amountMinor = Number(item.amountMinor);
        const kitQuantity = Number(item.kitQuantity);
        if (!Number.isInteger(amountMinor) || amountMinor <= 0 || !Number.isInteger(kitQuantity) || kitQuantity < 1) {
          console.warn(JSON.stringify({
            event: "commerce.cart.line_dropped",
            offerId: item.offerId,
            productId: item.productId || null,
            amountMinor,
            kitQuantity,
          }));
          return [];
        }
        const affiliateCode = /^[A-Za-z0-9_-]{4,40}$/.test(String(item.affiliateCode || ""))
          ? item.affiliateCode
          : null;
        return [{
          ...item,
          quantity: 1,
          amountMinor,
          kitQuantity,
          freeShipping: item.freeShipping === true,
          affiliateCode,
        }];
      }
      const quantity = boundedQuantity(item.quantity, item.maxQuantity);
      return quantity > 0 ? [{ ...item, quantity }] : [];
    });
  } catch (error) {
    console.error(JSON.stringify({
      event: "commerce.cart.unreadable",
      message: error?.message || String(error),
    }));
    return [];
  }
}

function cartItem(product, variant) {
  const availableQuantity = Number(variant.availableQuantity);
  return {
    id: `${product.id}:${variant.id}`,
    productId: product.id,
    productHandle: product.handle,
    variantId: variant.id,
    title: product.title,
    variantTitle: variant.title,
    options: variant.options || {},
    sku: variant.sku || null,
    image:
      product.primaryImage ||
      product.images?.find((image) => image.isPrimary)?.url ||
      product.images?.[0]?.url ||
      null,
    amountMinor: Number(variant.amountMinor),
    currency: variant.currency || "BRL",
    stockStatus: variant.stockStatus,
    maxQuantity:
      variant.stockStatus === "in_stock" && availableQuantity > 0
        ? availableQuantity
        : null,
    quantity: 1,
  };
}

function boundedQuantity(quantity, maxQuantity) {
  const normalized = Math.max(0, Math.trunc(Number(quantity) || 0));
  return maxQuantity == null
    ? normalized
    : Math.min(normalized, maxQuantity);
}

export function CartProvider({ storeSlug, children }) {
  const [items, setItems] = useState(() => readCart(storeSlug));

  useEffect(() => {
    localStorage.setItem(
      storageKey(storeSlug),
      JSON.stringify({ items, updatedAt: new Date().toISOString() })
    );
  }, [items, storeSlug]);

  const addItem = useCallback((product, variant) => {
    if (!product || !variant || variant.stockStatus === "out_of_stock") {
      return false;
    }
    const nextItem = cartItem(product, variant);
    setItems((current) => {
      const existing = current.find((item) => item.id === nextItem.id);
      if (!existing) return [...current, nextItem];
      return current.map((item) =>
        item.id === nextItem.id
          ? {
              ...item,
              ...nextItem,
              quantity: boundedQuantity(
                item.quantity + 1,
                nextItem.maxQuantity
              ),
            }
          : item
      );
    });
    return true;
  }, []);

  const addKit = useCallback((product, variant, kit, affiliateCode) => {
    if (!product || !variant || !kit?.id || variant.stockStatus === "out_of_stock") {
      return false;
    }
    const priceMinor = Number(kit.priceMinor);
    const kitQuantity = Number(kit.quantity);
    if (!Number.isInteger(priceMinor) || priceMinor <= 0) return false;
    if (!Number.isInteger(kitQuantity) || kitQuantity < 1) return false;
    const code = /^[A-Za-z0-9_-]{4,40}$/.test(String(affiliateCode || "")) ? affiliateCode : null;
    const nextItem = {
      ...cartItem(product, variant),
      id: `${product.id}:${variant.id}:kit:${kit.id}`,
      offerId: kit.id,
      quantity: 1,
      kitQuantity,
      amountMinor: priceMinor,
      freeShipping: kit.freeShipping === true,
      maxQuantity: null,
      affiliateCode: code,
    };
    setItems((current) => {
      const existing = current.find((item) => item.id === nextItem.id);
      if (!existing) return [...current, nextItem];
      if (code && existing.affiliateCode !== code) {
        return current.map((item) => (
          item.id === nextItem.id ? { ...item, affiliateCode: code } : item
        ));
      }
      return current;
    });
    return true;
  }, []);

  const replaceKit = useCallback((itemId, kit) => {
    const priceMinor = Number(kit?.priceMinor);
    const kitQuantity = Number(kit?.quantity);
    if (!kit?.id || !Number.isInteger(priceMinor) || priceMinor <= 0) return false;
    if (!Number.isInteger(kitQuantity) || kitQuantity < 1) return false;
    setItems((current) => {
      const source = current.find((item) => item.id === itemId && item.offerId);
      if (!source) return current;
      const nextId = `${source.productId}:${source.variantId}:kit:${kit.id}`;
      if (nextId === source.id) return current;
      if (current.some((item) => item.id === nextId)) {
        return current.filter((item) => item.id !== itemId);
      }
      return current.map((item) => (
        item.id === itemId
          ? {
              ...item,
              id: nextId,
              offerId: kit.id,
              quantity: 1,
              kitQuantity,
              amountMinor: priceMinor,
              freeShipping: kit.freeShipping === true,
              maxQuantity: null,
            }
          : item
      ));
    });
    return true;
  }, []);

  const setQuantity = useCallback((itemId, quantity) => {
    setItems((current) =>
      current.flatMap((item) => {
        if (item.id !== itemId || item.offerId) return [item];
        const nextQuantity = boundedQuantity(quantity, item.maxQuantity);
        return nextQuantity > 0 ? [{ ...item, quantity: nextQuantity }] : [];
      })
    );
  }, []);

  const removeItem = useCallback((itemId) => {
    setItems((current) => current.filter((item) => item.id !== itemId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const value = {
    items,
    itemCount: items.reduce((total, item) => total + item.quantity, 0),
    subtotalMinor: items.reduce((total, item) => total + lineAmount(item), 0),
    currency: items[0]?.currency || "BRL",
    addItem,
    addKit,
    replaceKit,
    setQuantity,
    removeItem,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return context;
}
